import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { auth, db } from '../../firebase';
import DashboardLayout from '../dashboard/_components/DashboardLayout';
import { collection, deleteDoc, doc, getDoc, getDocs, updateDoc } from 'firebase/firestore';
import { 
  User, Mail, Phone, Calendar, MapPin, Briefcase, 
  CreditCard, FileText, ArrowLeft, Edit, Trash2, 
  Home, Globe, Hash, Clock, Loader2, X, AlertTriangle
} from 'lucide-react';

const normalizeStringValue = (value) => {
  if (!value && value !== 0) {
    return '';
  }
  return value.toString().trim().toLowerCase();
};

const normalizeDigitsValue = (value) => {
  if (!value && value !== 0) {
    return '';
  }
  return value.toString().replace(/\D/g, '');
};

export default function CustomerDetail() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [user, setUser] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editForm, setEditForm] = useState(null);
  const [editSaving, setEditSaving] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [loanHistory, setLoanHistory] = useState([]);
  const [loanStats, setLoanStats] = useState({ total: 0, active: 0, disbursed: 0 });
  const [loanHistoryLoading, setLoanHistoryLoading] = useState(false);
  const [showLoanHistoryModal, setShowLoanHistoryModal] = useState(false);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((u) => {
      if (u) {
        setUser(u);
      } else {
        navigate('/login');
      }
    });
    return () => unsubscribe();
  }, [navigate]);

  useEffect(() => {
    if (user && id) {
      const fetchCustomer = async () => {
        setLoading(true);
        try {
          const customerDoc = await getDoc(doc(db, 'users', id));
          if (customerDoc.exists()) {
            setCustomer({ id: customerDoc.id, ...customerDoc.data() });
          } else {
            // Silent fail
            navigate('/customers');
          }
        } catch (error) {
          // Silent fail
          navigate('/customers');
        } finally {
          setLoading(false);
        }
      };
      fetchCustomer();
    }
  }, [user, id, navigate]);

  useEffect(() => {
    if (customer) {
      setEditForm({
        fullName: customer.fullName || customer.name || '',
        email: customer.email || '',
        phoneNumber: customer.phoneNumber || '',
        dateOfBirth: getDateInputValue(customer.dateOfBirth),
        gender: customer.gender || '',
        occupation: customer.occupation || '',
        address: customer.address || '',
        city: customer.city || '',
        state: customer.state || '',
        pincode: customer.pincode || '',
        aadharNumber: customer.aadharNumber || '',
        panNumber: customer.panNumber || '',
        status: customer.status || 'active'
      });
    }
  }, [customer]);

  const matchesCustomer = useCallback(
    (loanData = {}) => {
      if (!customer) {
        return false;
      }

      const customerUid = customer.uid || customer.id;
      const candidateUids = [
        loanData.uid,
        loanData.userId,
        loanData.userID,
        loanData.userUid,
        loanData.userUID,
        loanData.customerId,
        loanData.customerID,
        loanData.customerUid,
        loanData.customerUID,
        loanData.applicantId,
      ]
        .map((value) => (value ? value.toString() : null))
        .filter(Boolean);

      if (customerUid && candidateUids.some((value) => value === customerUid)) {
        return true;
      }

      if (customer.id && candidateUids.some((value) => value === customer.id)) {
        return true;
      }

      const normalizedCustomerEmail = normalizeStringValue(customer.email);
      const normalizedLoanEmails = [
        loanData.email,
        loanData.applicantEmail,
        loanData.businessEmail,
        loanData.ownerEmail,
      ]
        .map(normalizeStringValue)
        .filter(Boolean);

      if (normalizedCustomerEmail && normalizedLoanEmails.includes(normalizedCustomerEmail)) {
        return true;
      }

      const normalizedCustomerPhone = normalizeDigitsValue(customer.phoneNumber);
      const normalizedLoanPhones = [
        loanData.mobileNumber,
        loanData.phoneNumber,
        loanData.contactNumber,
        loanData.ownerPhone,
        loanData.customerPhone,
      ]
        .map(normalizeDigitsValue)
        .filter(Boolean);

      if (normalizedCustomerPhone && normalizedLoanPhones.includes(normalizedCustomerPhone)) {
        return true;
      }

      if (customer.aadharNumber && loanData.aadharNumber === customer.aadharNumber) {
        return true;
      }

      if (customer.panNumber && loanData.panNumber === customer.panNumber) {
        return true;
      }

      return false;
    },
    [customer]
  );

  useEffect(() => {
    if (!customer) {
      setLoanHistory([]);
      setLoanStats({ total: 0, active: 0, disbursed: 0 });
      return;
    }

    let isCancelled = false;

    const loadLoanHistory = async () => {
      setLoanHistoryLoading(true);
      try {
        const [personalSnapshot, businessSnapshot] = await Promise.all([
          getDocs(collection(db, 'personalLoan')),
          getDocs(collection(db, 'businessLoans')),
        ]);

        const buildRecord = (docSnap, type) => {
          const data = docSnap.data() || {};

          if (!matchesCustomer(data)) {
            return null;
          }

          let createdAt = null;
          try {
            if (data.createdAt?.toDate) {
              createdAt = data.createdAt.toDate();
            } else if (data.createdAt instanceof Date) {
              createdAt = data.createdAt;
            } else if (data.createdAt) {
              createdAt = new Date(data.createdAt);
            }
          } catch {
            createdAt = null;
          }

          const rawAmount = Number(
            data.loanAmount ?? data.amount ?? data.requestedAmount ?? data.disbursedAmount ?? 0
          );
          const amount = Number.isFinite(rawAmount) ? rawAmount : 0;

          const applicantName =
            type === 'Personal'
              ? data.fullName || data.applicantName || data.name || 'Personal Loan Applicant'
              : data.businessName || data.ownerName || data.fullName || data.applicantName || 'Business Loan Applicant';

          return {
            id: docSnap.id,
            type,
            applicant: applicantName,
            status: data.status || 'pending',
            createdAt,
            amount,
            route: type === 'Personal' ? `/personal-loans/${docSnap.id}` : `/business-loans/${docSnap.id}`,
          };
        };

        const personalLoans = personalSnapshot.docs
          .map((docSnap) => buildRecord(docSnap, 'Personal'))
          .filter(Boolean);

        const businessLoans = businessSnapshot.docs
          .map((docSnap) => buildRecord(docSnap, 'Business'))
          .filter(Boolean);

        const combined = [...personalLoans, ...businessLoans].sort((a, b) => {
          const timeA = a.createdAt ? a.createdAt.getTime() : 0;
          const timeB = b.createdAt ? b.createdAt.getTime() : 0;
          return timeB - timeA;
        });

        if (!isCancelled) {
          setLoanHistory(combined);
          const activeStatuses = new Set(['pending', 'under-review', 'approved', 'disbursed']);
          const disbursedStatuses = new Set(['approved', 'disbursed', 'completed']);

          const activeCount = combined.filter((loan) =>
            activeStatuses.has((loan.status || '').toLowerCase())
          ).length;

          const disbursedTotal = combined.reduce((sum, loan) => {
            const status = (loan.status || '').toLowerCase();
            if (disbursedStatuses.has(status)) {
              return sum + (Number.isFinite(loan.amount) ? loan.amount : 0);
            }
            return sum;
          }, 0);

          setLoanStats({
            total: combined.length,
            active: activeCount,
            disbursed: disbursedTotal,
          });
        }
      } catch (error) {
        // Silent fail
        if (!isCancelled) {
          setLoanHistory([]);
          setLoanStats({ total: 0, active: 0, disbursed: 0 });
        }
      } finally {
        if (!isCancelled) {
          setLoanHistoryLoading(false);
        }
      }
    };

    loadLoanHistory();

    return () => {
      isCancelled = true;
    };
  }, [customer, matchesCustomer]);

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    try {
      if (timestamp.toDate) {
        return timestamp.toDate().toLocaleDateString('en-IN', { 
          day: 'numeric', 
          month: 'long', 
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        });
      }
      return new Date(timestamp).toLocaleDateString('en-IN', { 
        day: 'numeric', 
        month: 'long', 
        year: 'numeric' 
      });
    } catch {
      return 'N/A';
    }
  };

  const getDateInputValue = (dob) => {
    if (!dob) return '';
    try {
      if (dob.toDate) {
        return dob.toDate().toISOString().split('T')[0];
      }
      return new Date(dob).toISOString().split('T')[0];
    } catch {
      return '';
    }
  };

  const formatDateOfBirth = (dob) => {
    if (!dob) return 'N/A';
    try {
      const date = new Date(dob);
      return date.toLocaleDateString('en-IN', {
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      });
    } catch {
      return dob;
    }
  };

  const calculateAge = (dob) => {
    if (!dob) return null;
    try {
      const birthDate = new Date(dob);
      const today = new Date();
      let age = today.getFullYear() - birthDate.getFullYear();
      const monthDiff = today.getMonth() - birthDate.getMonth();
      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
        age--;
      }
      return age;
    } catch {
      return null;
    }
  };

  const formatCurrency = (amount) => {
    const numericAmount = Number(amount);
    if (!Number.isFinite(numericAmount) || numericAmount <= 0) {
      return '₹0';
    }

    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(numericAmount);
  };

  const getLoanStatusBadge = (status) => {
    const normalized = (status || '').toLowerCase();
    const styles = {
      approved: 'bg-green-100 text-green-800',
      disbursed: 'bg-purple-100 text-purple-800',
      completed: 'bg-gray-100 text-gray-800',
      pending: 'bg-yellow-100 text-yellow-800',
      'under-review': 'bg-blue-100 text-blue-800',
      rejected: 'bg-red-100 text-red-800',
    };
    return styles[normalized] || 'bg-gray-100 text-gray-800';
  };

  const getLoanStatusLabel = (status) => {
    if (!status) {
      return 'Pending';
    }
    return status.charAt(0).toUpperCase() + status.slice(1).replace('-', ' ');
  };

  const handleEditChange = (field, value) => {
    setEditForm((prev) => (prev ? { ...prev, [field]: value } : prev));
  };

  const handleEditSubmit = async (event) => {
    event.preventDefault();
    if (!customer || !editForm || editSaving) return;

    setEditSaving(true);
    try {
      const sanitize = (value) => (typeof value === 'string' ? value.trim() : value || '');
      const payload = {
        fullName: sanitize(editForm.fullName),
        name: sanitize(editForm.fullName) || customer.name || '',
        email: sanitize(editForm.email),
        phoneNumber: sanitize(editForm.phoneNumber),
        dateOfBirth: editForm.dateOfBirth || '',
        gender: editForm.gender || '',
        occupation: sanitize(editForm.occupation),
        address: sanitize(editForm.address),
        city: sanitize(editForm.city),
        state: sanitize(editForm.state),
        pincode: sanitize(editForm.pincode),
        aadharNumber: sanitize(editForm.aadharNumber),
        panNumber: sanitize(editForm.panNumber),
        status: editForm.status || 'active',
        updatedAt: new Date()
      };

      const customerRef = doc(db, 'users', customer.id);
      await updateDoc(customerRef, payload);

      setCustomer((prev) => (prev ? { ...prev, ...payload } : prev));
      setFeedback({ type: 'success', message: 'Customer details updated successfully.' });
      setShowEditModal(false);
    } catch (error) {
      // Silent fail
      setFeedback({ type: 'error', message: 'Failed to update customer. Please try again.' });
    } finally {
      setEditSaving(false);
    }
  };

  const handleDeleteCustomer = async () => {
    if (!customer || deleteLoading) return;

    setDeleteLoading(true);
    try {
      await deleteDoc(doc(db, 'users', customer.id));
      setFeedback({ type: 'success', message: 'Customer deleted successfully.' });
      setShowDeleteConfirm(false);
      navigate('/customers');
    } catch (error) {
      // Silent fail
      setFeedback({ type: 'error', message: 'Failed to delete customer. Please try again.' });
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleLoanNavigate = useCallback(
    (route) => {
      if (!route) {
        return;
      }

      setShowLoanHistoryModal(false);
      navigate(route);
    },
    [navigate]
  );

  if (!user || loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-600">Loading customer details...</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (!customer) return null;

  const InfoCard = ({ icon, label, value, fullWidth = false }) => {
    const Icon = icon;
    return (
      <div className={`${fullWidth ? 'col-span-full' : ''}`}>
        <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors">
          <div className="p-2 bg-white rounded-lg shrink-0">
            <Icon className="w-5 h-5 text-gray-600" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">{label}</p>
            <p className="text-sm font-semibold text-gray-900 wrap-break-word">{value || 'Not provided'}</p>
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <DashboardLayout>
        {feedback && (
        <div
          className={`mb-4 sm:mb-6 rounded-xl border px-4 py-3 text-sm ${
            feedback.type === 'error'
              ? 'border-red-200 bg-red-50 text-red-700'
              : 'border-green-200 bg-green-50 text-green-700'
          }`}
        >
          {feedback.message}
        </div>
        )}
      {/* Header */}
      <div className="mb-6 sm:mb-8">
        <button
          onClick={() => navigate('/customers')}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4 transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
          <span className="font-medium">Back to Customers</span>
        </button>
        
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-start gap-4">
            <div className="w-16 h-16 sm:w-20 sm:h-20 bg-blue-600 rounded-2xl flex items-center justify-center text-white text-2xl sm:text-3xl font-bold shrink-0">
              {customer.fullName ? customer.fullName.charAt(0).toUpperCase() : '?'}
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
                {customer.fullName || customer.name || 'N/A'}
              </h1>
              <p className="text-gray-500 text-sm mt-1">Customer ID: {customer.uid || customer.id}</p>
              <div className="flex items-center gap-2 mt-2">
                <span className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                  customer.status === 'inactive' 
                    ? 'bg-red-100 text-red-800' 
                    : 'bg-green-100 text-green-800'
                }`}>
                  {customer.status || 'Active'}
                </span>
                {customer.gender && (
                  <span className="px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                    {customer.gender === 'male' ? '👨 Male' : '👩 Female'}
                  </span>
                )}
              </div>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => editForm && setShowEditModal(true)}
              disabled={!editForm}
              className="flex items-center gap-2 px-4 py-2.5 bg-blue-600 text-white rounded-xl hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
            >
              <Edit className="w-4 h-4" />
              <span className="text-sm font-medium">Edit</span>
            </button>
            <button
              type="button"
              onClick={() => setShowDeleteConfirm(true)}
              disabled={deleteLoading}
              className="flex items-center gap-2 px-4 py-2.5 bg-red-600 text-white rounded-xl hover:bg-red-700 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
            >
              {deleteLoading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Trash2 className="w-4 h-4" />
              )}
              <span className="text-sm font-medium hidden sm:inline">
                {deleteLoading ? 'Deleting...' : 'Delete'}
              </span>
            </button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Personal Information */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
            <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
              <User className="w-5 h-5 text-blue-600" />
              Personal Information
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <InfoCard icon={User} label="Full Name" value={customer.fullName || customer.name} />
              <InfoCard icon={Calendar} label="Date of Birth" value={
                customer.dateOfBirth 
                  ? `${formatDateOfBirth(customer.dateOfBirth)}${calculateAge(customer.dateOfBirth) ? ` (${calculateAge(customer.dateOfBirth)} years)` : ''}`
                  : 'N/A'
              } />
              <InfoCard icon={Globe} label="Gender" value={customer.gender ? (customer.gender.charAt(0).toUpperCase() + customer.gender.slice(1)) : 'N/A'} />
              <InfoCard icon={Briefcase} label="Occupation" value={customer.occupation} />
            </div>
          </div>

          {/* Contact Information */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
            <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
              <Mail className="w-5 h-5 text-blue-600" />
              Contact Information
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <InfoCard icon={Mail} label="Email Address" value={customer.email} />
              <InfoCard icon={Phone} label="Phone Number" value={customer.phoneNumber} />
            </div>
          </div>

          {/* Address Information */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
            <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
              <MapPin className="w-5 h-5 text-blue-600" />
              Address Information
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <InfoCard icon={Home} label="Address" value={customer.address} fullWidth={!customer.city && !customer.state} />
              <InfoCard icon={MapPin} label="City" value={customer.city} />
              <InfoCard icon={Globe} label="State" value={customer.state} />
              <InfoCard icon={Hash} label="Pincode" value={customer.pincode} />
            </div>
          </div>

          {/* Identity Documents */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
            <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-blue-600" />
              Identity Documents
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <InfoCard 
                icon={FileText} 
                label="Aadhar Number" 
                value={customer.aadharNumber || 'Not provided'} 
              />
              <InfoCard 
                icon={CreditCard} 
                label="PAN Number" 
                value={customer.panNumber || 'Not provided'} 
              />
            </div>
            {(!customer.aadharNumber || !customer.panNumber) && (
              <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-sm text-yellow-800">
                  ⚠️ Some identity documents are missing. Please request the customer to complete their profile.
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Account Timeline */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
            <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
              <Clock className="w-5 h-5 text-blue-600" />
              Account Timeline
            </h2>
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <div className="p-2 bg-green-100 rounded-lg shrink-0">
                  <Calendar className="w-4 h-4 text-green-600" />
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Created</p>
                  <p className="text-sm font-semibold text-gray-900 mt-1">
                    {formatDate(customer.createdAt)}
                  </p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <div className="p-2 bg-blue-100 rounded-lg shrink-0">
                  <Clock className="w-4 h-4 text-blue-600" />
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Last Updated</p>
                  <p className="text-sm font-semibold text-gray-900 mt-1">
                    {formatDate(customer.updatedAt)}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Quick Stats */}
          <div className="bg-linear-to-br from-blue-600 via-blue-700 to-purple-700 rounded-2xl shadow-lg p-6 text-white">
            <h2 className="text-lg font-bold mb-4">Quick Stats</h2>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-blue-100 text-sm">Total Loans</span>
                <span className="text-2xl font-bold flex items-center justify-end gap-2">
                  {loanHistoryLoading ? (
                    <Loader2 className="w-5 h-5 animate-spin text-white" />
                  ) : (
                    loanStats.total
                  )}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-blue-100 text-sm">Active Loans</span>
                <span className="text-2xl font-bold flex items-center justify-end gap-2">
                  {loanHistoryLoading ? (
                    <Loader2 className="w-5 h-5 animate-spin text-white" />
                  ) : (
                    loanStats.active
                  )}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-blue-100 text-sm">Total Disbursed</span>
                <span className="text-2xl font-bold flex items-center justify-end gap-2">
                  {loanHistoryLoading ? (
                    <Loader2 className="w-5 h-5 animate-spin text-white" />
                  ) : (
                    formatCurrency(loanStats.disbursed)
                  )}
                </span>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setShowLoanHistoryModal(true)}
              className="w-full mt-4 px-4 py-2 bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-lg text-sm font-medium transition-colors border border-white/20 disabled:opacity-60 disabled:cursor-not-allowed"
              disabled={loanHistoryLoading && loanHistory.length === 0}
            >
              {loanHistoryLoading && loanHistory.length === 0 ? (
                <span className="flex items-center justify-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>Fetching history…</span>
                </span>
              ) : (
                'View Loan History'
              )}
            </button>
          </div>

          {/* Actions */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
            <h2 className="text-lg font-bold text-gray-900 mb-4">Actions</h2>
            <div className="space-y-2">
              <button className="w-full px-4 py-2.5 bg-gray-50 hover:bg-gray-100 text-gray-700 rounded-xl text-sm font-medium transition-colors text-left">
                📄 View Documents
              </button>
              <button className="w-full px-4 py-2.5 bg-gray-50 hover:bg-gray-100 text-gray-700 rounded-xl text-sm font-medium transition-colors text-left">
                💬 Send Message
              </button>
              <button className="w-full px-4 py-2.5 bg-gray-50 hover:bg-gray-100 text-gray-700 rounded-xl text-sm font-medium transition-colors text-left">
                📊 View Activity Log
              </button>
              <button className="w-full px-4 py-2.5 bg-gray-50 hover:bg-gray-100 text-gray-700 rounded-xl text-sm font-medium transition-colors text-left">
                🔒 Reset Password
              </button>
            </div>
          </div>
        </div>
      </div>
      </DashboardLayout>

      {showLoanHistoryModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4 py-6">
          <div
            className="absolute inset-0 bg-gray-900/40"
            onClick={() => setShowLoanHistoryModal(false)}
          ></div>
          <div className="relative w-full max-w-5xl bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden">
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 bg-gray-50">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Loan History</h3>
                <p className="text-xs text-gray-500 mt-0.5">
                  {(customer.fullName || customer.name || 'This customer')}’s loan activity across all products.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setShowLoanHistoryModal(false)}
                className="p-1.5 rounded-lg hover:bg-gray-200 transition-colors"
              >
                <X className="w-4 h-4 text-gray-600" />
              </button>
            </div>

            <div className="px-5 py-5 space-y-5 max-h-[75vh] overflow-y-auto">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="rounded-xl border border-gray-100 bg-gray-50 px-4 py-3">
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">Total Loans</p>
                  <p className="text-xl font-bold text-gray-900 mt-1">{loanStats.total}</p>
                </div>
                <div className="rounded-xl border border-gray-100 bg-gray-50 px-4 py-3">
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">Active Loans</p>
                  <p className="text-xl font-bold text-gray-900 mt-1">{loanStats.active}</p>
                </div>
                <div className="rounded-xl border border-gray-100 bg-gray-50 px-4 py-3">
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">Total Disbursed</p>
                  <p className="text-xl font-bold text-gray-900 mt-1">{formatCurrency(loanStats.disbursed)}</p>
                </div>
              </div>

              {loanHistoryLoading ? (
                <div className="flex flex-col items-center justify-center py-16">
                  <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
                  <p className="text-sm text-gray-600 font-medium mt-3">Loading loan history…</p>
                </div>
              ) : loanHistory.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 text-center">
                  <FileText className="w-12 h-12 text-gray-300 mb-3" />
                  <p className="text-sm font-medium text-gray-600">No loan applications found for this customer.</p>
                  <p className="text-xs text-gray-400 mt-1">Any applications submitted in the future will appear here automatically.</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Loan Type</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Applicant</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Amount</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Status</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Applied On</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Action</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-100 text-sm">
                      {loanHistory.map((loan) => (
                        <tr key={`${loan.type}-${loan.id}`} className="hover:bg-gray-50 transition-colors">
                          <td className="px-4 py-3 whitespace-nowrap font-semibold text-gray-900">{loan.type}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-gray-600">{loan.applicant || 'N/A'}</td>
                          <td className="px-4 py-3 whitespace-nowrap font-semibold text-gray-900">{formatCurrency(loan.amount)}</td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <span className={`px-2.5 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getLoanStatusBadge(loan.status)}`}>
                              {getLoanStatusLabel(loan.status)}
                            </span>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-gray-600">{formatDate(loan.createdAt)}</td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <button
                              type="button"
                              onClick={() => handleLoanNavigate(loan.route)}
                              className="text-blue-600 hover:text-blue-700 font-medium"
                            >
                              View Details
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {showDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4 py-6">
          <div
            className="absolute inset-0 bg-gray-900/40"
            onClick={() => {
              if (!deleteLoading) {
                setShowDeleteConfirm(false);
              }
            }}
          ></div>
          <div className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden">
            <div className="flex items-center gap-3 px-5 py-4 border-b border-gray-100 bg-red-50">
              <div className="p-2 bg-red-100 rounded-lg">
                <AlertTriangle className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Delete customer?</h3>
                <p className="text-xs text-gray-500 mt-0.5">This action permanently removes the customer record.</p>
              </div>
            </div>

            <div className="px-5 py-6 space-y-4">
              <p className="text-sm text-gray-600">
                Are you sure you want to delete <span className="font-semibold text-gray-900">{customer.fullName || customer.name || 'this customer'}</span>? This cannot be undone.
              </p>
              <div className="flex items-center justify-end gap-3">
                <button
                  type="button"
                  onClick={() => !deleteLoading && setShowDeleteConfirm(false)}
                  className="px-4 py-2 rounded-lg border border-gray-200 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                  disabled={deleteLoading}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleDeleteCustomer}
                  disabled={deleteLoading}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
                >
                  {deleteLoading && <Loader2 className="w-4 h-4 animate-spin" />}
                  <span>{deleteLoading ? 'Deleting...' : 'Delete'}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showEditModal && editForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4 py-6">
          <div
            className="absolute inset-0 bg-gray-900/40"
            onClick={() => {
              if (!editSaving) {
                setShowEditModal(false);
              }
            }}
          ></div>
          <div className="relative w-full max-w-3xl bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden">
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 bg-gray-50">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Edit Customer</h3>
                <p className="text-xs text-gray-500 mt-0.5">Update the customer profile information below.</p>
              </div>
              <button
                type="button"
                onClick={() => !editSaving && setShowEditModal(false)}
                className="p-1.5 rounded-lg hover:bg-gray-200 transition-colors"
                disabled={editSaving}
              >
                <X className="w-4 h-4 text-gray-600" />
              </button>
            </div>

            <form onSubmit={handleEditSubmit} className="px-5 py-6 max-h-[75vh] overflow-y-auto space-y-6">
              <div>
                <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-3">Personal Details</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 uppercase tracking-wider mb-1" htmlFor="fullName">Full Name</label>
                    <input
                      id="fullName"
                      type="text"
                      value={editForm.fullName}
                      onChange={(e) => handleEditChange('fullName', e.target.value)}
                      className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 uppercase tracking-wider mb-1" htmlFor="email">Email</label>
                    <input
                      id="email"
                      type="email"
                      value={editForm.email}
                      onChange={(e) => handleEditChange('email', e.target.value)}
                      className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 uppercase tracking-wider mb-1" htmlFor="phoneNumber">Phone Number</label>
                    <input
                      id="phoneNumber"
                      type="tel"
                      value={editForm.phoneNumber}
                      onChange={(e) => handleEditChange('phoneNumber', e.target.value)}
                      className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 uppercase tracking-wider mb-1" htmlFor="dateOfBirth">Date of Birth</label>
                    <input
                      id="dateOfBirth"
                      type="date"
                      value={editForm.dateOfBirth}
                      onChange={(e) => handleEditChange('dateOfBirth', e.target.value)}
                      className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 uppercase tracking-wider mb-1" htmlFor="gender">Gender</label>
                    <select
                      id="gender"
                      value={editForm.gender}
                      onChange={(e) => handleEditChange('gender', e.target.value)}
                      className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    >
                      <option value="">Select</option>
                      <option value="male">Male</option>
                      <option value="female">Female</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 uppercase tracking-wider mb-1" htmlFor="occupation">Occupation</label>
                    <input
                      id="occupation"
                      type="text"
                      value={editForm.occupation}
                      onChange={(e) => handleEditChange('occupation', e.target.value)}
                      className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 uppercase tracking-wider mb-1" htmlFor="status">Status</label>
                    <select
                      id="status"
                      value={editForm.status}
                      onChange={(e) => handleEditChange('status', e.target.value)}
                      className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    >
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                    </select>
                  </div>
                </div>
              </div>

              <div>
                <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-3">Address</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="sm:col-span-2">
                    <label className="block text-xs font-medium text-gray-600 uppercase tracking-wider mb-1" htmlFor="address">Address</label>
                    <textarea
                      id="address"
                      rows={3}
                      value={editForm.address}
                      onChange={(e) => handleEditChange('address', e.target.value)}
                      className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm resize-none"
                    ></textarea>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 uppercase tracking-wider mb-1" htmlFor="city">City</label>
                    <input
                      id="city"
                      type="text"
                      value={editForm.city}
                      onChange={(e) => handleEditChange('city', e.target.value)}
                      className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 uppercase tracking-wider mb-1" htmlFor="state">State</label>
                    <input
                      id="state"
                      type="text"
                      value={editForm.state}
                      onChange={(e) => handleEditChange('state', e.target.value)}
                      className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 uppercase tracking-wider mb-1" htmlFor="pincode">Pincode</label>
                    <input
                      id="pincode"
                      type="text"
                      value={editForm.pincode}
                      onChange={(e) => handleEditChange('pincode', e.target.value)}
                      className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    />
                  </div>
                </div>
              </div>

              <div>
                <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-3">Identity</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 uppercase tracking-wider mb-1" htmlFor="aadharNumber">Aadhar Number</label>
                    <input
                      id="aadharNumber"
                      type="text"
                      value={editForm.aadharNumber}
                      onChange={(e) => handleEditChange('aadharNumber', e.target.value)}
                      className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 uppercase tracking-wider mb-1" htmlFor="panNumber">PAN Number</label>
                    <input
                      id="panNumber"
                      type="text"
                      value={editForm.panNumber}
                      onChange={(e) => handleEditChange('panNumber', e.target.value)}
                      className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    />
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => !editSaving && setShowEditModal(false)}
                  className="px-4 py-2 rounded-lg border border-gray-200 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                  disabled={editSaving}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={editSaving}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
                >
                  {editSaving && <Loader2 className="w-4 h-4 animate-spin" />}
                  <span>{editSaving ? 'Saving...' : 'Save Changes'}</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}
